import api from './api';
import { paymentDashboardService } from './paymentDashboardService';
import { getReportIncidents } from './reportIncidentService';

const REVENUE_PAGE_SIZE = 1000;
const REVENUE_MAX_PAGES = 30;

// Timezone-less payment dates are stored in Turkey time.
export const toTurkeyHourKey = (value) => {
  if (typeof value !== 'string') return null;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return `${new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul', hour: '2-digit', hourCycle: 'h23'
    }).format(date)}:00`;
  }
  const match = value.match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}):\d{2}/);
  return match && Number(match[1]) < 24 ? `${match[1]}:00` : null;
};

/** Local YYYY-MM-DD (avoids UTC shift from toISOString). */
export const toDateKey = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Extract the date part from API date values like "2026-10-14T00:00:00". */
export const normalizeDateKey = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : toDateKey(parsed);
};

export const buildDateRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  const keys = [];
  const cursor = new Date(start);
  while (toDateKey(cursor) <= toDateKey(end)) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return { startKey: toDateKey(start), endKey: toDateKey(end), keys };
};

/** Works with the different response shapes used across the backend. */
const extractItems = (res) => {
  const candidates = [res, res?.data, res?.Data, res?.data?.data];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
    if (Array.isArray(c?.items)) return c.items;
    if (Array.isArray(c?.Items)) return c.Items;
  }
  return [];
};

const extractCount = (res) => {
  const candidates = [res, res?.data, res?.Data, res?.data?.data];
  for (const c of candidates) {
    const total = c?.totalCount ?? c?.TotalCount;
    if (total !== undefined && total !== null && !Number.isNaN(Number(total))) {
      return Number(total);
    }
  }
  return extractItems(res).length;
};

/** Runs a loader, measuring latency and capturing failures without throwing. */
const timed = async (loader) => {
  const started = performance.now();
  try {
    const value = await loader();
    return { ok: true, ms: Math.round(performance.now() - started), value };
  } catch (error) {
    return { ok: false, ms: Math.round(performance.now() - started), error };
  }
};

const slotHourKey = (value) => {
  const match = String(value ?? '').match(/^(\d{1,2})(?::[0-5]\d(?::[0-5]\d(?:\.\d+)?)?)?$/);
  return match && Number(match[1]) < 24 ? `${match[1].padStart(2, '0')}:00` : null;
};

export const normalizeHourlyCounts = (source, expectedCount) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
  const hourly = {};
  for (const [hour, value] of Object.entries(source)) {
    const key = slotHourKey(hour);
    const count = Number(value);
    if (!key || !Number.isInteger(count) || count < 0) return null;
    hourly[key] = (hourly[key] || 0) + count;
  }
  return Object.values(hourly).reduce((sum, count) => sum + count, 0) === expectedCount ? hourly : null;
};

const loadAppointmentSummary = async (path, range) => {
  const res = await api.get(path, { params: { days: range.keys.length } });
  if (res?.isSuccess === false) {
    throw new Error(res.resultMessage || 'Randevu özeti alınamadı');
  }
  const data = res?.data || res;
  if (!data || !Number.isFinite(data.rangeCount) || !data.daily || !data.status) {
    throw new Error('Randevu özeti geçersiz');
  }
  // The old summary uses scheduled dates/slots and cannot describe creation activity.
  if (data.dateBasis !== 'created') {
    throw new Error('Randevu oluşturulma verileri için API güncellemesi gerekiyor.');
  }
  if (range.keys.length !== 1) return data;
  const hourly = normalizeHourlyCounts(data.hourly, data.rangeCount);
  if (hourly) return { ...data, hourly };
  if (data.rangeCount === 0) return { ...data, hourly: {} };
  let hourlyError = 'Toplamlar alındı; servis saatlik oluşturulma dağılımını döndürmedi.';
  if (data.hourly && typeof data.hourly === 'object' && !Array.isArray(data.hourly)) {
    const entries = Object.entries(data.hourly);
    const valid = entries.every(([hour, count]) => slotHourKey(hour) && Number.isInteger(Number(count)) && Number(count) >= 0);
    hourlyError = valid
      ? `Saatlik dağılımda ${entries.reduce((sum, [, count]) => sum + Number(count), 0)} kayıt var; toplam ${data.rangeCount} randevu ile eşleşmiyor.`
      : 'Toplamlar alındı; saatlik dağılımın saat veya adet bilgileri geçersiz.';
  }
  return { ...data, hourly: null, hourlyError };
};

const loadSports = (range) => loadAppointmentSummary('/api/sports/appointments/dashboard', range);
const loadLibrary = (range) => loadAppointmentSummary('/api/admin/library-appointments/dashboard', range);
/**
 * Stats endpoint has no daily series, so daily revenue is built from the
 * successful transactions list (CreatedAt is returned in Turkey local time).
 */
const fetchDailyRevenue = async ({ startKey, endKey }) => {
  const filters = { startDate: startKey, endDate: endKey, status: 'success' };
  const fetchPage = (page) =>
    paymentDashboardService.getPaymentTransactionsPaged(page, REVENUE_PAGE_SIZE, filters);

  const first = await fetchPage(1);
  const totalCount = extractCount(first);
  const items = [...extractItems(first)];
  const totalPages = Math.min(Math.ceil(totalCount / REVENUE_PAGE_SIZE), REVENUE_MAX_PAGES);

  for (let p = 2; p <= totalPages; p += 1) {
    // Sequential: each page triggers several enrichment queries on the backend.
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchPage(p);
    items.push(...extractItems(res));
  }

  const daily = {};
  items.forEach((tx) => {
    const key = startKey === endKey ? toTurkeyHourKey(tx.createdAt) : normalizeDateKey(tx.createdAt);
    if (!key) return;
    daily[key] = (daily[key] || 0) + Number(tx.amount || 0);
  });
  return daily;
};

const loadRevenue = async (range) => {
  const res = await paymentDashboardService.getPaymentDashboardStats({
    startDate: range.startKey,
    endDate: range.endKey
  });
  if (res && res.isSuccess === false) {
    throw new Error(res.resultMessage || 'Gelir verisi alınamadı');
  }
  const data = res?.data || res || {};
  return {
    totalRevenue: Number(data.totalRevenue || 0),
    todayRevenue: Number(data.todayRevenue || 0),
    successfulTransactions: Number(data.successfulTransactions || 0),
    totalTransactions: Number(data.totalTransactions || 0)
  };
};

const loadIncidents = async () => {
  const res = await getReportIncidents();
  if (res && res.isSuccess === false) {
    throw new Error(res.resultMessage || 'Bildirimler alınamadı');
  }
  const items = extractItems(res);
  const byStatus = { New: 0, InProgress: 0, Resolved: 0, Rejected: 0 };
  items.forEach((i) => {
    const s = i.status || 'New';
    byStatus[s] = (byStatus[s] || 0) + 1;
  });
  return { total: items.length, byStatus };
};

// Share in-flight work, including React StrictMode's repeated mount effect.
const inFlight = new Map();

export const getDashboardOverview = (days = 1, onProgress) => {
  const existing = inFlight.get(days);
  if (existing) {
    if (onProgress) {
      existing.listeners.add(onProgress);
      onProgress(existing.snapshot);
    }
    return existing.promise;
  }

  const range = buildDateRange(days);
  const entry = {
    snapshot: { range },
    listeners: new Set(onProgress ? [onProgress] : [])
  };
  const publish = () => entry.listeners.forEach((listener) => listener(entry.snapshot));
  const loaders = {
    sports: () => loadSports(range),
    library: () => loadLibrary(range),
    revenue: () => loadRevenue(range),
    revenueDaily: () => fetchDailyRevenue(range),
    incidents: loadIncidents
  };

  inFlight.set(days, entry);
  publish();
  entry.promise = Promise.all(Object.entries(loaders).map(async ([key, loader]) => {
    const result = await timed(loader);
    entry.snapshot = { ...entry.snapshot, [key]: result, fetchedAt: new Date() };
    publish();
  })).then(() => entry.snapshot).finally(() => {
    inFlight.delete(days);
    entry.listeners.clear();
  });
  return entry.promise;
};
